// ロード中・空・エラー（再試行）・準備中の共通表示。どの画面も無反応・白画面で放置しない。
import { META_CONFIG } from '@hyakki/core';
import { ApiError } from '../api';
import { sfx, type SfxKey } from '../audio';
import { act } from '../state';
import { toast } from './overlay';
import { esc } from './dom';
import { icon } from './icons';

export const loadingHtml = (text = '読み込み中…') =>
  `<div class="state-box loading" role="status"><div class="spinner" aria-hidden="true"></div><p>${esc(text)}</p></div>`;

export const notReadyHtml = (what: string) =>
  `<div class="state-box notready">${icon('clock')}<p>${esc(what)}は準備中です。</p></div>`;

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.offline) return '通信できませんでした。回線を確認してください。';
    if (e.status === 401) return 'ログインの有効期限が切れました。';
    if (e.status === 503 && e.code === 'maintenance') return 'メンテナンス中です。';
    if (e.status >= 500) return 'サーバーが混み合っています。少し待ってからもう一度お試しください。';
    const map: Record<string, string> = {
      hyoro: '兵糧が足りません。',
      komedoiya_required: '米問屋の通い帳が必要です。',
      kajiya_level: '鍛冶屋のLvを上げると強化できます。',
      ticket_cap: `掃討チケットは${META_CONFIG.sweep.ticketCap}枚までしか持てません。`,
      stamina: '兵糧が足りません。',
      zeni: '銭が足りません。',
      reidama: '霊玉が足りません。',
      kuji: 'ガチャ券が足りません。',
      equipped: '装備中のお守りは使えません。',
      paid: '購入品のお守りは売却・錬成できません。',
      need3: '素材を3つ選んでください。',
      daily_limit: '今日の分は売り切れです。明日また並びます。',
      item_level: '主Lvが足りません。',
      owned: '解放済みです。',
      zaimoku: '材木が足りません。',
      smith: '里の鍛冶屋のLvを上げると限界突破できます。',
      locked: 'まだ挑戦できません。',
      busy: '別の戦闘が進行中です。',
      capacity: 'ポイントが足りません。',
      requires: '前提のスキルを先に装着してください。',
      not_owned: 'ショップで買うと使えます。',
      empty: 'この枠にはまだ保存していません。',
      min: '下限に足りません。',
      name_length: '名前は2〜12文字にしてください。',
      name_chars: '名前に使えない文字が含まれています。',
      name_ng: 'その名前は使えません。',
      name_same: '今と同じ名前です。',
      monthly_required: '月パスの期間中に選べます。',
      auto_not_owned: 'オートプレイの権利がありません。',
      already_done: 'この操作はすでに反映されています。',
      invalid_commands: 'ゲームが更新されたため、この戦闘の結果を確かめられませんでした。兵糧は返しました。画面を再読み込みしてください。',
      guest: 'Google連携すると使えます。',
      guest_chapter: 'ゲストで遊べるのは第1章クリアまでです。Google連携すると続きを遊べます。',
      stage_locked: 'まだ挑戦できません。前の戦場を勝つと進めます。',
      session_closed: 'この戦闘はもう終わっています。',
      active_session: '別の戦闘が進行中です。TOPの「戦闘に戻る」から続けられます。',
      sold_out_today: '今日の分は売り切れです。明日また並びます。',
      already_owned: '持っています。',
      pass_full: `残りが${META_CONFIG.monthly.buyWithinDays}日を切ると延長できます。`,
      already_linked: 'すでにGoogle連携しています。',
      google_in_use: 'このGoogleアカウントは別のデータで使われています。',
      event_closed: 'このイベントは終わりました。',
      survey_closed: 'このアンケートは締め切りました。',
      revision_changed: 'ほかの端末で内容が変わりました。画面を再読み込みしてください。',
      text_empty: '文字を入れてください。',
      text_length: '文字が長すぎます。',
      text_lines: '行が多すぎます。',
      text_ng: '使えない言葉が含まれています。',
      text_url: 'URLは送れません。',
      text_chars: '使えない文字が含まれています。',
      too_many: '数が多すぎます。',
    };
    if (map[e.code]) return map[e.code];
    if (e.status === 409) return 'ほかの端末・タブと操作が重なりました。もう一度お試しください。';
    if (e.status === 429) return '操作が続きすぎています。少し待ってからお試しください。';
    return `エラーが発生しました（${e.status} ${e.code}）。`;
  }
  return 'エラーが発生しました。';
}

/** 読み込み → 描画。失敗時は再試行ボタン付きのエラー、404 は「準備中」。 */
export async function loadInto<T>(el: HTMLElement, what: string, load: () => Promise<T>, render: (data: T) => void) {
  el.innerHTML = loadingHtml();
  try {
    const data = await load();
    render(data);
  } catch (e) {
    if (e instanceof ApiError && e.notReady) {
      el.innerHTML = notReadyHtml(what);
      return;
    }
    el.innerHTML = `<div class="state-box error" role="alert">${icon('info')}<p>${esc(errorText(e))}</p><button class="btn primary" data-retry>${icon('auto')}再試行</button></div>`;
    el.querySelector('[data-retry]')!.addEventListener('click', () => void loadInto(el, what, load, render));
  }
}

/** 操作をサーバーへ送って /api/me を取り直す。成功で ok を出し（se があれば鳴らす）、失敗ならメッセージ。成否を返す */
export async function runOp(p: Promise<unknown>, ok?: string, se?: SfxKey) {
  try {
    await act(p);
    if (se) sfx(se);
    if (ok) toast(ok, 'good');
    return true;
  } catch (e) {
    sfx('error');
    toast(errorText(e), 'error');
    return false;
  }
}
